import { Format, TabsValueChangeDetails } from '@ark-ui/react'
import { UsageWithCredits } from '@integration-app/sdk'
import prettyMilliseconds from 'pretty-ms'

import { DataTable } from 'components/DataTable'
import { Tabs } from 'ui-kit/tabs'
import { Text } from 'ui-kit/text'
import { convertKebabCaseToTitleCase } from 'utils/case'

export function UsageStats({
  usage,
  isLoading,
  selectedInterval,
  handleIntervalChange,
}: {
  usage: UsageWithCredits[]
  isLoading: boolean
  selectedInterval: string
  handleIntervalChange: (details: TabsValueChangeDetails) => void
}) {
  const thirtyDayTotalCredits = usage.reduce(
    (acc, curr) => acc + (curr.credits ?? 0),
    0,
  )
  return (
    <Tabs.Root value={selectedInterval} onValueChange={handleIntervalChange}>
      <Tabs.List>
        <Tabs.Trigger value='today'>Today</Tabs.Trigger>
        <Tabs.Trigger value='yesterday'>Yesterday</Tabs.Trigger>
        <Tabs.Trigger value='lastThirtyDays'>Last 30 Days</Tabs.Trigger>
        <Tabs.Indicator />
      </Tabs.List>
      <Tabs.Content value={selectedInterval}>
        <Text as={'h4'} fontWeight={'bold'}>
          Total Credits Used:{' '}
          {thirtyDayTotalCredits.toLocaleString('en-US', {
            maximumFractionDigits: 1,
          })}
        </Text>
        <DataTable.Root
          width={'full'}
          maxWidth={'lg'}
          tableLayout={'fixed'}
          variant={'outline'}
          marginTop={4}
          value={usage ?? []}
          isLoading={isLoading}
          rowComponent={({ instance }) => <UsageRow instance={instance} />}
        >
          <DataTable.Header width={'60%'}>Usage Type</DataTable.Header>
          <DataTable.Header textWrap={'nowrap'} textAlign='right'>
            Usage Value
          </DataTable.Header>
          <DataTable.Header textWrap={'nowrap'} textAlign='right'>
            Credits
          </DataTable.Header>
        </DataTable.Root>
      </Tabs.Content>
    </Tabs.Root>
  )
}

function UsageRow({ instance }: { instance: UsageWithCredits }) {
  return (
    <DataTable.Row key={instance.key}>
      <DataTable.Cell paddingBlock={1.5}>
        {convertKebabCaseToTitleCase(instance.key)}
      </DataTable.Cell>
      <DataTable.Cell
        textWrap={'nowrap'}
        textAlign='right'
        fontVariantNumeric='tabular-nums'
      >
        {instance.key.endsWith('-size') ? (
          <Format.Byte value={instance.value ?? 0} />
        ) : instance.key.endsWith('-duration') ? (
          prettyMilliseconds((instance.value ?? 0) * 1000, {
            hideYearAndDays: true,
            hideSeconds: true,
          })
        ) : (
          new Intl.NumberFormat('en', {
            notation: 'compact',
            maximumFractionDigits: 1,
          }).format(instance.value ?? 0)
        )}
      </DataTable.Cell>
      <DataTable.Cell textAlign='right' fontVariantNumeric='tabular-nums'>
        {(instance.credits ?? 0).toLocaleString('en-US', {
          maximumFractionDigits: 1,
        })}
      </DataTable.Cell>
    </DataTable.Row>
  )
}
